// allauth-headless.types.ts
// Auto-derived from django-allauth 65.x headless browser client contract
// Endpoint base: /api/_allauth/browser/v1/

// ---------------------------------------------------------------------------
// Primitive building blocks
// ---------------------------------------------------------------------------

/** All flow IDs that allauth headless can return in data.flows */
export type AllauthFlowId =
  | 'login'
  | 'login_by_code'
  | 'signup'
  | 'verify_email'
  | 'verify_phone'
  | 'reauthenticate'
  | 'mfa_authenticate' // pending: TOTP / recovery-code step required
  | 'mfa_trust' // pending: trust-this-browser step required (MFA_TRUST_ENABLED=true only)
  | 'mfa_login_webauthn'
  | 'mfa_reauthenticate'
  | 'mfa_signup_webauthn'
  | 'provider_redirect'
  | 'provider_signup'
  | 'provider_token'
  | 'password_reset_by_code'

/** MFA authenticator types allauth supports */
export type AllauthMFAType = 'totp' | 'recovery_codes' | 'webauthn'

// ---------------------------------------------------------------------------
// Shared wrappers (every response uses these)
// ---------------------------------------------------------------------------

/** Envelope returned by every allauth headless endpoint */
export interface AllauthResponse<TData = unknown, TMeta = AllauthBaseMeta> {
  status: number
  data?: TData
  meta?: TMeta
  errors?: AllauthError[]
}

export interface AllauthBaseMeta {
  is_authenticated: boolean | null
  /** Browser client only: present when session is fresh / rotated */
  session_token?: string
}

/** C5P extension: added by AllauthSessionEnrichmentMiddleware (KLO-545) */
export interface AllauthSessionMeta extends AllauthBaseMeta {
  requires_mfa_setup: boolean
}

export interface AllauthError {
  message: string
  code: string
  /** Set to field name when error is field-specific */
  param?: string
}

// ---------------------------------------------------------------------------
// User object (data.user on authenticated responses)
// ---------------------------------------------------------------------------

export interface AllauthUser {
  id: number | string
  display: string
  email: string | null
  has_usable_password: boolean
  username?: string
}

// ---------------------------------------------------------------------------
// Flow objects (data.flows on 401 responses)
// ---------------------------------------------------------------------------

export interface AllauthFlow {
  id: AllauthFlowId
  /** true when this flow is the *current* required next step */
  is_pending?: true
}

export interface AllauthMFAAuthenticateFlow extends AllauthFlow {
  id: 'mfa_authenticate'
  /** Authenticator types the user has configured */
  types: AllauthMFAType[]
}

export interface AllauthProviderFlow extends AllauthFlow {
  id: 'provider_redirect' | 'provider_token'
  providers: string[]
}

export type AllauthAnyFlow =
  | AllauthMFAAuthenticateFlow
  | AllauthProviderFlow
  | AllauthFlow

// ---------------------------------------------------------------------------
// Authentication method record (data.methods on 200 responses)
// ---------------------------------------------------------------------------

export interface AllauthAuthMethod {
  method: 'password' | 'socialaccount' | 'mfa' | 'login_by_code'
  at: number // Unix timestamp
  email?: string
  provider?: string
  uid?: string
  type?: AllauthMFAType
  id?: number
}

// ---------------------------------------------------------------------------
// C5P-specific session enrichment (KLO-545 / KLO-631 / KLO-749)
// Added by AllauthSessionEnrichmentMiddleware on login + session endpoints only
// ---------------------------------------------------------------------------

export type AllauthMFAStatus =
  | 'configured'
  | 'required_not_configured'
  | 'optional'
  | 'exempt'

export interface AllauthMFAStatusData {
  status: AllauthMFAStatus
  enforced: boolean
  configured: boolean
  setup_required: boolean
  exempt: boolean
}

export interface AllauthHijackData {
  is_hijacked: true
  hijacker: {
    id: string
    email: string
    name: string | null
  }
}

export interface AllauthSuperuserData {
  is_superuser: boolean
}

// ---------------------------------------------------------------------------
// Concrete response shapes per endpoint
// ---------------------------------------------------------------------------

/** 401 — login incomplete, next step in data.flows */
export interface AllauthUnauthenticatedData {
  flows: AllauthAnyFlow[]
}

/** 200 — fully authenticated */
export interface AllauthAuthenticatedData {
  user: AllauthUser
  methods: AllauthAuthMethod[]
  /** C5P only — present on login + session responses */
  mfa?: AllauthMFAStatusData
  /** C5P only — present when admin is hijacking a user */
  hijack?: AllauthHijackData
  /** C5P only — present when session user is a superuser */
  superuser?: AllauthSuperuserData
}

// POST /api/_allauth/browser/v1/auth/login
// GET|POST /api/_allauth/browser/v1/auth/session
// POST /api/_allauth/browser/v1/auth/2fa/authenticate
// POST /api/_allauth/browser/v1/auth/2fa/trust
export type AllauthAuthResponse =
  | AllauthResponse<AllauthAuthenticatedData, AllauthSessionMeta> // 200
  | AllauthResponse<AllauthUnauthenticatedData, AllauthBaseMeta> // 401

// POST /api/_allauth/browser/v1/auth/2fa/authenticate  (invalid code)
export type AllauthMFAAuthenticateErrorResponse = AllauthResponse<
  never,
  AllauthBaseMeta
> // 400, errors[].code === "incorrect_code"

// ---------------------------------------------------------------------------
// Request bodies
// ---------------------------------------------------------------------------

export interface AllauthLoginRequest {
  email: string
  password: string
}

export interface AllauthMFAAuthenticateRequest {
  code: string
  /** Optional — allauth only reads `code`; safe to include */
  type?: AllauthMFAType
}

export interface AllauthMFATrustRequest {
  /** true → set mfa_trusted cookie (~14 days); false → no cookie, MFA every login */
  trust: boolean
}

// ---------------------------------------------------------------------------
// Type guards
// ---------------------------------------------------------------------------

export function isAuthenticated(
  res: AllauthAuthResponse | null | undefined,
): res is AllauthResponse<AllauthAuthenticatedData, AllauthSessionMeta> {
  if (!res || typeof res !== 'object') return false
  return res.status === 200 && res.meta?.is_authenticated === true
}

export function getPendingFlow(
  res: AllauthAuthResponse | null | undefined,
): AllauthAnyFlow | undefined {
  if (!res || typeof res !== 'object') return undefined
  if (res.status !== 401) return undefined
  const data = res.data as AllauthUnauthenticatedData | undefined
  return data?.flows?.find((f) => f.is_pending === true)
}

export function isMFAAuthenticatePending(
  res: AllauthAuthResponse | null | undefined,
): res is AllauthResponse<AllauthUnauthenticatedData, AllauthBaseMeta> {
  return getPendingFlow(res)?.id === 'mfa_authenticate'
}

export function isMFATrustPending(
  res: AllauthAuthResponse | null | undefined,
): res is AllauthResponse<AllauthUnauthenticatedData, AllauthBaseMeta> {
  return getPendingFlow(res)?.id === 'mfa_trust'
}

export function isIncorrectCode(
  res: AllauthAuthResponse | null | undefined,
): boolean {
  if (!res || typeof res !== 'object') return false
  return (
    res.status === 400 &&
    (res.errors ?? []).some((e) => e.code === 'incorrect_code')
  )
}

// ---------------------------------------------------------------------------
// MFA authenticator management
// ---------------------------------------------------------------------------

export interface AllauthTOTPAuthenticator {
  type: 'totp'
  created_at: number
  last_used_at: number | null
}

export interface AllauthRecoveryCodesAuthenticator {
  type: 'recovery_codes'
  created_at: number
  last_used_at: number | null
  total_code_count: number
  unused_code_count: number
  /** Only present when fetched with sensitive flag (POST to regenerate) */
  unused_codes?: string[]
}

export type AllauthAuthenticator =
  | AllauthTOTPAuthenticator
  | AllauthRecoveryCodesAuthenticator

// GET /api/_allauth/browser/v1/account/authenticators
export type AllauthAuthenticatorsResponse = AllauthResponse<
  AllauthAuthenticator[]
>

// GET|POST /api/_allauth/browser/v1/account/authenticators/totp
export type AllauthTOTPResponse =
  | AllauthResponse<AllauthTOTPAuthenticator> // 200 — active
  | AllauthResponse<never, { secret: string; totp_url: string }> // 404 — not yet configured

// ---------------------------------------------------------------------------
// Config endpoint
// ---------------------------------------------------------------------------

export interface AllauthConfigMFA {
  supported_types: AllauthMFAType[]
  passkey_login_enabled: boolean
}

export interface AllauthConfigSocialAccount {
  providers: AllauthSocialAccountProvider[]
}

export interface AllauthConfig {
  account: {
    authentication_method: 'email' | 'username' | 'username_email'
    is_open_for_signup: boolean
    login_methods: string[]
    email_verification_by_code_enabled: boolean
    login_by_code_enabled: boolean
    password_reset_by_code_enabled: boolean
  }
  mfa?: AllauthConfigMFA
  socialaccount?: AllauthConfigSocialAccount
}

export type AllauthConfigResponse = AllauthResponse<AllauthConfig>

// ---------------------------------------------------------------------------
// Social account types (GET /api/_allauth/browser/v1/account/providers)
// ---------------------------------------------------------------------------

export interface AllauthSocialAccountProvider {
  id: string
  name: string
  flows: string[]
  client_id?: string
}

export interface AllauthSocialAccount {
  uid: string
  provider: AllauthSocialAccountProvider
  display: string
}

export type AllauthSocialAccountsResponse = AllauthResponse<
  AllauthSocialAccount[]
>
